/** API request/response types — SPEC §3. */

export const PROVIDERS = ['aws', 'gcp', 'azure', 'cloudflare'] as const;
export type Provider = (typeof PROVIDERS)[number];
export const REGION_PROVIDERS = ['aws', 'gcp', 'azure'] as const;
export type RegionProvider = (typeof REGION_PROVIDERS)[number];

export function isProvider(s: unknown): s is Provider {
  return typeof s === 'string' && (PROVIDERS as readonly string[]).includes(s);
}
export function isRegionProvider(p: Provider): p is RegionProvider {
  return p !== 'cloudflare';
}

export type LocationKind = 'region' | 'colo';
export type LocationStatus = 'active' | 'no_signal' | 'unreachable' | 'retired';
export type ProbeMode = 'cors' | 'opaque';
export type { Confidence } from './weights.ts';

export const COLLECTED_PRECISION = 5;
export const MIN_MAP_PRECISION = 2;
export const MAX_MAP_PRECISION = 5;

export interface MetaResponse {
  build: number;
  build_at: number | null;
  hydrate_at: number | null;
  turnstile_site_key: string;
  half_life_days: number;
}

/** §3.1 — parallel arrays with a hoisted code dictionary. */
export interface MapResponse {
  provider: Provider;
  precision: number;
  prefix: string;
  build: number;
  codes: string[];
  cells: string[];
  w: number[];
  s: number[];
  t: number[];
  /** runner-up index into `codes`, or -1 when uncontested */
  r: number[];
  /** share of `t` resting on cold-start plans (§5.5) */
  b: number[];
}

export interface LocationEntry {
  code: string;
  name: string | null;
  lat: number;
  lon: number;
  kind: LocationKind;
  status: LocationStatus;
}

export interface LocationsResponse {
  provider: Provider;
  build: number;
  locations: LocationEntry[];
}

export interface ProbeCandidate {
  code: string;
  url: string;
  mode: ProbeMode;
  expect_status: number | null;
}

export interface ProbeBudget {
  total_ms: number;
  per_request_ms: number;
  warm_concurrency: number;
  timed_concurrency: number;
  /** how many of the fastest-to-warm candidates get timed samples (§5.2) */
  screen_top: number;
}

export interface ProbesResponse {
  provider: RegionProvider;
  plan_id: string;
  budget: ProbeBudget;
  candidates: ProbeCandidate[];
}

export interface ColoSubmission {
  provider: 'cloudflare';
  cell: string;
  turnstile: string;
}

export interface RegionSubmission {
  provider: RegionProvider;
  cell: string;
  code: string;
  rtt_ms: number;
  confidence: import('./weights.ts').Confidence;
  plan_id: string;
  turnstile: string;
  /** the winner's 1-based position in the warm-round ranking, for §12; never stored per visit */
  warm_rank?: number;
}

export type Submission = ColoSubmission | RegionSubmission;

export type ObservationResponse =
  | { accepted: true; weight: number; colo: string; name: string | null }
  | { accepted: false; reason: string };

export interface ApiError {
  error: string;
  detail: string;
}
